import type { CSSProperties, ReactNode } from "react";
import { NavLink } from "react-router";
import { useLocale } from "@genius-tracker/i18n/react";
import { MOBILE_BOTTOM_ITEMS } from "./navModel.js";
import { MoreIcon } from "./NavIcons.js";
import { prefetchRoute } from "../routePrefetch.js";
import { ALL_MODULES_ACTIVE, type ModuleView } from "../modules/useModules.js";

/**
 * GT-APP-SHELL-2-NAVIGATION-AND-FLEET-PULSE-01 (DL-122) — mobile bottom
 * navigation. The owner-approved mobile concept: a fixed bottom bar with the
 * highest-frequency destinations (Genius · Map · Vehicles · Alerts) plus a
 * "More" button that opens the drawer with the full grouped nav. Designed for
 * mobile, not a squeezed desktop sidebar.
 *
 * Accessibility: a `<nav>` landmark, `aria-current="page"` on the active item
 * (via `NavLink`), a labelled "More" button with `aria-expanded`/`aria-controls`,
 * and ≥ 44 px touch targets. Honours the iOS home-indicator safe area.
 */
export function MobileBottomNav({
  onMoreClick,
  moreOpen,
  drawerId,
  modules = ALL_MODULES_ACTIVE,
}: {
  readonly onMoreClick: () => void;
  readonly moreOpen: boolean;
  readonly drawerId: string;
  /**
   * A-MODUL-NAV1 — Modulsicht aus der Shell. Vorbelegung "alles aktiv", damit
   * die Leiste auch ohne Shell-Kontext (Tests, Vorschauen) vollstaendig
   * rendert; die Shell reicht die echte Sicht durch.
   */
  readonly modules?: ModuleView;
}): ReactNode {
  const { t } = useLocale();
  // Ausgeblendete Module verschwinden aus der Leiste. Basis (Karte, Fahrten,
  // Uebersicht) bleibt immer; Genius Intelligence nur, solange nicht hidden.
  const items = MOBILE_BOTTOM_ITEMS.filter((item) => !modules.isNavItemHidden(item));
  return (
    <nav aria-label={t("nav.bottom.aria")} data-testid="mobile-bottom-nav" style={barStyle}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.exact ?? false}
          // E-PREFETCH-1 — auf dem Telefon gibt es kein Hover, aber der
          // Tastaturfokus (und Hover auf Tablets) zieht den Chunk vor.
          onMouseEnter={() => prefetchRoute(item.to)}
          onFocus={() => prefetchRoute(item.to)}
          data-testid={`mobile-bottom-${item.to.replace(/^\//, "").replace(/\//g, "-")}`}
          style={({ isActive }) => ({
            ...slotStyle,
            color: isActive ? "var(--gt-sys-accent)" : "var(--gt-sys-text-muted)",
            background:
              isActive && item.to === "/intelligence"
                ? "var(--gt-sys-ai-command-surface)"
                : "transparent",
            boxShadow:
              isActive && item.to === "/intelligence"
                ? "var(--gt-sys-ai-command-glow-soft)"
                : "none",
          })}
        >
          <span aria-hidden style={iconWrapStyle}>
            <item.Icon />
          </span>
          <span style={labelStyle}>{t(item.labelKey)}</span>
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMoreClick}
        aria-expanded={moreOpen}
        aria-controls={drawerId}
        aria-label={t("nav.more")}
        data-testid="mobile-bottom-more"
        style={{
          ...slotStyle,
          appearance: "none",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: moreOpen ? "var(--gt-sys-accent)" : "var(--gt-sys-text-muted)",
        }}
      >
        <span aria-hidden style={iconWrapStyle}>
          <MoreIcon />
        </span>
        <span style={labelStyle}>{t("nav.more")}</span>
      </button>
    </nav>
  );
}

const barStyle: CSSProperties = {
  position: "sticky",
  insetBlockEnd: 0,
  zIndex: 40,
  display: "flex",
  alignItems: "stretch",
  justifyContent: "space-around",
  gap: 2,
  background: "color-mix(in oklab, var(--gt-sys-surface) 96%, transparent)",
  backdropFilter: "saturate(180%) blur(10px)",
  borderBlockStart: "1px solid var(--gt-sys-border)",
  paddingBlockStart: 4,
  paddingBlockEnd: "max(4px, env(safe-area-inset-bottom))",
  paddingInline: 4,
};

const slotStyle: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 2,
  minBlockSize: 48,
  paddingBlock: 4,
  borderRadius: "var(--gt-sys-radius-md)",
  textDecoration: "none",
  fontSize: "var(--gt-sys-font-size-xs)",
};

const iconWrapStyle: CSSProperties = { display: "inline-flex" };

const labelStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: "var(--gt-sys-font-weight-medium)",
  lineHeight: 1,
  maxInlineSize: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
